import { trackConfig, type IFramework } from '@/framework';
import { TextMeasurer } from '@/utils/text-kit';
import {
    LayerType,
    TextWritingType,
    type ILayerProps,
    type IVectorLayerProps,
    type VectorTextInfo,
} from '@/version-types';
import { pushHistory, updateLayer } from '../utils';

export interface ITextDecoration {
    /**
     * 设置下划线
     *
     * @param layerID - 图层 ID
     * @param flag - 是否标记
     */
    setUnderline(layerID: string, flag: boolean): ILayerProps | void;

    /**
     * 设置删除线
     *
     * @param layerID - 图层 ID
     * @param flag - 是否标记
     */
    setLineThrough(layerID: string, flag: boolean): ILayerProps | void;
}

export class TextDecoration implements ITextDecoration {
    constructor(private framework: IFramework) {}

    private resolveConflict(textInfo: VectorTextInfo, kind: 'underline' | 'lineThrough', flag: boolean) {
        if (!flag) return textInfo;

        const conflictType = kind === 'underline' ? 'lineThrough' : 'underline';
        const nextTextInfo = { ...textInfo };

        nextTextInfo[conflictType] = false;

        return nextTextInfo;
    }

    private calcLayout(layer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = layer;
        const layerData = layer.data as IVectorLayerProps;
        const textInfo = layerData.textInfo as VectorTextInfo;

        const { writingMode } = textInfo;
        const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

        const textSize = TextMeasurer.measure(layer, {
            keepConstantWidth: isHorizontal,
            keepConstantHeight: !isHorizontal,
        });

        const nextLayerWidth = Math.max(layerWidth, textSize.width);
        const nextLayerHeight = Math.max(layerHeight, textSize.height);

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    private set(layerID: string, kind: 'underline' | 'lineThrough', flag: boolean) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        let nextTextInfo: VectorTextInfo = { ...textInfo, [kind]: flag };
        nextTextInfo = this.resolveConflict(nextTextInfo, kind, flag);

        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { width: nextLayerWidth, height: nextLayerHeight } = this.calcLayout(nextLayer);
        nextLayer.width = nextLayerWidth;
        nextLayer.height = nextLayerHeight;

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);

        return nextLayer;
    }

    setUnderline(layerID: string, flag: boolean) {
        return this.set(layerID, 'underline', flag);
    }

    setLineThrough(layerID: string, flag: boolean) {
        return this.set(layerID, 'lineThrough', flag);
    }
}
